<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Button, Container, Label, Loader, Select, Text, toast } from '@Shelly/shelly-design-system';
	import { disable_skill, get_active_integrations, postReq } from '../../../services/http';
	import { open_amazon_linking_android } from '../../../services/native_container_requests_android';
	import { open_amazon_linking_ios } from '../../../services/native_container_requests_ios';
	import { user_data } from 'src/stores/account/user';
	import { active_integrations, skill_stage } from 'src/stores/integrations/alexa';
	import { devices } from 'src/stores/lists/devices';
	import { app, os } from 'src/stores/session/native-bridge';
	import { ALEXA_SKILLS_STAGES } from './constants_and_helpers';

	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	import type { Device } from '../../../types/Device';
	import { navigateToUrl } from '../../Lists/list-helpers';
	import { loadingData } from 'src/stores/ui/loading-data';
	import { capFirstChar } from 'src/helpers/utilities';
	import TransitionWrapper from '../../Utility/TransitionWrapper.svelte';

	import { isDevServerUser } from 'src/helpers/user_server_conditions';

	export let error: boolean | string = false,
		err_msg: boolean | string = false,
		bottomMargin: boolean = false;

	const devices_list: Device[] = Object.values($devices).filter((d: Device) => !d.shared);
	const relays: Device[] = devices_list.filter((d: Device) => d.category === 'relay');
	const lights: Device[] = devices_list.filter((d: Device) => d.category === 'light');
	const rollers: Device[] = devices_list.filter((d: Device) => d.category === 'roller');

	const predefined = () => {
		let arr = [];
		if (relays.length > 0) {
			arr.push(
				`"${$format('label_alexa_command_on', { default: 'Alexa, turn on' })} ${relays[0].name}"`,
			);
		}
		if (lights.length > 0) {
			arr.push(
				`"${$format('label_alexa_command_set', { default: 'Alexa, set' })} ${lights[0].name} ${$format(
					'label_alexa_to_75%_brightness',
					{
						default: 'to 75% brightness',
					},
				)}"`,
			);
		}

		if (rollers.length > 0) {
			arr.push(
				`"${$format('label_alexa_command_open', { default: 'Alexa, open' })} ${rollers[0].name}"`,
			);
		}

		if (arr.length === 0) {
			arr.push(
				`"${$format('label_alexa_turn_on_lights', {
					default: 'Alexa, turn on all lights',
				})}`,
				`"${$format('label_alexa_open_curtains', {
					default: 'Alexa, open small curtains',
				})}"`,
			);
		}

		return arr;
	};

	const host = import.meta.env.VITE_APP_BUCKET || window.location.host;
	let msg = '',
		sub_msg = '',
		examples = [],
		content = '';
	$: user_connected = $active_integrations.includes('alexa');
	$: user_connected && prepare_page_massages();

	let loading = false;

	const handle_enable = async () => {
		loading = true;

		const geturls: {
			isok: boolean;
			data?: { app_url: string; web_url: string };
		} = await postReq('/integrated_api/app_to_app_urls', {
			client: 'alexa',
			type: $skill_stage,
			app: $app,
			host: 'control',
		});

		if (geturls.isok) {
			let { app_url, web_url } = geturls.data;

			if (
				typeof app_url !== 'string' ||
				typeof web_url !== 'string' ||
				!app_url.match(/https/) ||
				!web_url.match(/https/)
			) {
				return toast.open({ type: 'danger', title: $format('error_global') });
			}

			if ($app) {
				if ($os === 'IOS') {
					open_amazon_linking_ios(app_url, web_url);
				} else {
					open_amazon_linking_android(app_url, web_url);
				}
			} else {
				// let open = `https://www.amazon.com/ap/oa?client_id=${ClientId}&scope=alexa::skills:account_linking&response_type=code&state=${yourState}&redirect_uri=${yourRedirectUrl}`;
				navigateToUrl(web_url);
			}
		} else {
			console.error('there was an error getting alexa skill urls', geturls);
		}

		loading = false;
	};

	const handle_disable = async () => {
		loading = true;

		const res = await disable_skill({
			client: 'alexa',
			type: $skill_stage,
			host,
		});
		if (res.isok) {
			active_integrations.remove('alexa');
			// navigateToUrl('/settings/app-to-app-alexa');
		}
		loading = false;
	};

	const handle_click = () => {
		if (user_connected) {
			return handle_disable();
		} else {
			return handle_enable();
		}
	};

	const prepare_page_massages = () => {
		console.log('prepare_page_massages');

		msg = user_connected
			? $format('title_alexa_linked_account', {
					default: 'your account is linked with alexa',
				})
			: typeof error === 'string'
				? error
				: $format('title_alexa_connect_account', {
						default: 'connect your account with alexa',
					});

		sub_msg = user_connected
			? $format('label_alexa_how_to_use', { default: 'How to use it: ' })
			: typeof err_msg === 'string'
				? err_msg
				: '';

		examples = user_connected ? predefined() : [];

		content =
			!user_connected && (!err_msg || examples.length === 0)
				? $format('label_alexa_instructions', {
						default:
							'Enable Shelly Cloud Skill and link your account with Alexa to easy control devices with your voice. You can unlink your account at any time, just disable Shelly Cloud  Skill from Amazon Alexa app or from Shelly app.',
					})
				: typeof err_msg === 'string'
					? err_msg
					: '';
	};

	onMount(async () => {
		prepare_page_massages();
		if (!$skill_stage) {
			skill_stage.set('prod');
		}
	});

	// select for skill stage only available for test users
	const stages = Object.values(ALEXA_SKILLS_STAGES).map((stage: string) => ({
		title: $format(`label_alexa_${stage}`, { default: stage }),
		value: stage,
	}));

	$: selected_stage = $skill_stage;

	const onChangeEvent = (e: CustomEvent) => {
		skill_stage.set(e.detail.value);
	};
</script>

{#if isDevServerUser(['repo', 't1', 'nb'])}
	<Select
		class="width-full mb-m"
		label="Select prod or dev stage(skill)"
		value={selected_stage}
		on:change={onChangeEvent}
		options={stages}
		icon=""
		id="select-e1pdmwf"
		aria-labelledby="select-e1pdmwf"
	/>
{/if}

<Container class={bottomMargin ? 'mb-s' : ''}>
	{#if !$loadingData}
		<TransitionWrapper>
			<div class="d-flex flex-direction-column align-items-center">
				<Text element="h1" styleType="h1" class="mb-l text-center">{msg}</Text>

				<img
					alt="app linking"
					class="mb-l max-width-full"
					src={`images/app-linking/shelly-alexa-${user_connected ? 'linked' : 'unlinked'}.svg`}
				/>

				<Label
					class="mb-l"
					type={user_connected ? 'success' : 'danger'}
					size="m"
					icon={`fas fa-${user_connected ? 'check' : 'xmark'}`}
					value={capFirstChar(
						user_connected
							? $format('connected', { default: 'connected' })
							: $format('label_not_connected', { default: 'not connected' }),
					)}
				/>

				{#if sub_msg != ''}
					<Text element="h2" class="mb-m text-center">{sub_msg}</Text>
				{/if}

				{#if examples.length > 0}
					{#each examples as ex, exIndex}
						<Text
							class={classMap({
								'text-center': true,
								'mb-s': exIndex < examples.length - 1,
								'mb-m': exIndex == examples.length - 1,
							})}
						>
							{ex}
						</Text>
					{/each}
				{/if}

				{#if content != ''}
					<Text class="mb-l text-left width-full">{content}</Text>
				{/if}

				<Button
					class="width-md-auto"
					variant={user_connected ? 'secondary' : 'primary'}
					size="l"
					{loading}
					on:click={handle_click}
				>
					{user_connected
						? $format('button_disable', { default: 'Disable' })
						: $format('button_enable', { default: 'Enable' })}
				</Button>
			</div>
		</TransitionWrapper>
	{:else}
		<TransitionWrapper>
			<Loader class="mb-m" />
		</TransitionWrapper>
	{/if}
</Container>
